import React, { createContext, useContext, useEffect, useState } from 'react';
import { Button } from '@mantine/core';
import { getCookie, setCookie } from 'cookies-next';
import Link from 'next/link';
import SingletonRouter, { useRouter } from 'next/router';
import Script from 'next/script';

import { getAuthCookieOptions } from '@/utils/authCookies';
import { COOKIE_CONSENT } from '@/utils/constants';
import { installCustomerChatTagGuard } from '@/utils/customerChatTagGuard';
import PageLink from '@/utils/PageLink';

type ConsentValue = 'granted' | 'denied';

// Re-prompt after 6 months, matching the privacy policy's re-consent cadence.
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180;

type CookieConsentContextValue = {
  consent: ConsentValue | null;
  /** False until the stored decision (or GPC signal) has been read on the client. */
  consentResolved: boolean;
  bannerOpen: boolean;
  setConsent: (value: ConsentValue) => void;
  openSettings: () => void;
};

// No-op defaults so components using the hook (e.g. Footer) stay safe in
// isolated renders such as unit tests.
const CookieConsentContext = createContext<CookieConsentContextValue>({
  consent: null,
  consentResolved: false,
  bannerOpen: false,
  setConsent: () => undefined,
  openSettings: () => undefined,
});

// Consent Mode v2 signals. Everything starts denied; only "Accept" grants.
const CONSENT_SIGNALS = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
] as const;

const consentSignals = (value: ConsentValue) =>
  Object.fromEntries(CONSENT_SIGNALS.map((signal) => [signal, value]));

/** Forwards a consent decision to the Google tag (a no-op until GoogleTag initializes it). */
const updateGoogleConsent = (value: ConsentValue) => {
  window.gtag?.('consent', 'update', consentSignals(value));
};

export const useCookieConsent = () => useContext(CookieConsentContext);

/** Best-effort removal of Google Analytics and Google Ads cookies after consent is withdrawn. */
const deleteGaCookies = () => {
  if (typeof document === 'undefined') return;

  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0]?.trim())
    .filter((name) => name && (name.startsWith('_ga') || name.startsWith('_gcl')));

  const { hostname } = window.location;
  const domains = [''];
  // GA sets its cookies on the eTLD+1 (e.g. .chat.co), so try parent domains too.
  const parts = hostname.split('.');
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`.${parts.slice(i).join('.')}`);
  }

  names.forEach((name) => {
    domains.forEach((domain) => {
      const domainAttr = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}`;
    });
  });
};

type CookieConsentProviderProps = {
  children: React.ReactNode;
  /**
   * Leaves consent untouched: no cookie read or written, no Google cookie
   * deletion, banner never opens. For pages that serve a customer's visitors
   * (see isCustomerChatSurface), where chat.co has no consent to manage — on a
   * customer's custom domain the GPC purge would otherwise delete the
   * customer's own _ga/_gcl cookies.
   */
  disabled?: boolean;
};

export const CookieConsentProvider = ({
  children,
  disabled = false,
}: CookieConsentProviderProps) => {
  const [consent, setConsentState] = useState<ConsentValue | null>(null);
  const [consentResolved, setConsentResolved] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  // Read the stored decision on the client only, to avoid hydration mismatches.
  useEffect(() => {
    if (disabled) {
      // Also clears what an earlier chat.co page in this document resolved.
      setConsentState(null);
      setConsentResolved(false);
      setBannerOpen(false);
      return;
    }

    setConsentResolved(true);
    const stored = getCookie(COOKIE_CONSENT);

    // Honor Global Privacy Control as an opt-out, per our privacy policy.
    // The live browser signal overrides even a previously stored grant.
    if (typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl) {
      if (stored !== 'denied') {
        setCookie(COOKIE_CONSENT, 'denied', {
          ...getAuthCookieOptions(),
          maxAge: CONSENT_MAX_AGE,
        });
        deleteGaCookies();
      }
      setConsentState('denied');
      return;
    }

    if (stored === 'granted' || stored === 'denied') {
      setConsentState(stored);
      return;
    }

    setBannerOpen(true);
  }, [disabled]);

  const setConsent = (value: ConsentValue) => {
    if (disabled) return;
    setCookie(COOKIE_CONSENT, value, { ...getAuthCookieOptions(), maxAge: CONSENT_MAX_AGE });
    setBannerOpen(false);

    // GoogleTag forwards the new state to Consent Mode, which stops the running
    // tag from using cookies — withdrawing consent no longer needs a reload.
    if (value === 'denied') {
      // Revoke first so the tag cannot rewrite what we are about to delete.
      updateGoogleConsent('denied');
      // Purge identifiers already set, including those from visitors who
      // accepted earlier or predate consent gating.
      deleteGaCookies();
    }

    setConsentState(value);
  };

  const openSettings = () => {
    if (!disabled) setBannerOpen(true);
  };

  return (
    <CookieConsentContext.Provider
      value={{ consent, consentResolved, bannerOpen, setConsent, openSettings }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

type GoogleTagProps = {
  gaId: string;
  /** Google Ads account tag (AW-…); its config also builds remarketing audiences. */
  adsId?: string;
};

/**
 * Queues the Consent Mode defaults and tag configs, once per page load. Runs
 * before any consent update so the queue always opens with everything denied.
 */
const initGoogleTag = (ids: string[]) => {
  if (window.dataLayer) return;

  window.dataLayer = [];
  window.gtag = function gtag() {
    // gtag.js only accepts the `arguments` object, not a spread array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  // Give a returning visitor's stored "Accept" time to arrive before the
  // first hits are sent.
  window.gtag('consent', 'default', { ...consentSignals('denied'), wait_for_update: 500 });
  // Strip ad click identifiers from hits sent without ad consent, and carry
  // them across page loads in the URL instead of cookies.
  window.gtag('set', 'ads_data_redaction', true);
  window.gtag('set', 'url_passthrough', true);
  window.gtag('js', new Date());
  ids.forEach((id) => window.gtag?.('config', id));
};

/**
 * Loads a single Google tag (gtag.js) for Analytics and, optionally, Google Ads
 * conversion tracking and remarketing, under Google Consent Mode v2. The tag
 * loads for every visitor but sets no cookies until they accept; before that,
 * and after a decline or a GPC signal, Google receives only cookieless pings.
 */
export const GoogleTag = ({ gaId, adsId }: GoogleTagProps) => {
  const { consent } = useCookieConsent();
  const router = useRouter();

  useEffect(() => {
    const ids = [gaId, adsId].filter((id): id is string => Boolean(id));
    initGoogleTag(ids);
    // For the rest of this document, reaching a customer chat page becomes a
    // full page load (the guard outlives this component, e.g. on pages that
    // don't mount GoogleTag). It patches the real router instance: useRouter()
    // returns a per-render public wrapper whose methods forward to it, so <Link>,
    // useRouter() and window.next.router all go through the patch.
    installCustomerChatTagGuard(SingletonRouter.router ?? router, ids);
  }, [gaId, adsId, router]);

  useEffect(() => {
    if (consent) updateGoogleConsent(consent);
  }, [consent]);

  // gtag.js sends a page view only on its initial config. GA4 picks up
  // client-side route changes itself (enhanced measurement); Google Ads does
  // not, so forward each navigation. Consent Mode decides what it may carry.
  useEffect(() => {
    if (!adsId) return undefined;

    const handleRouteChange = () => {
      window.gtag?.('event', 'page_view', {
        send_to: adsId,
        page_location: window.location.href,
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [adsId, router.events]);

  return (
    <Script
      id="google-tag"
      src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
      strategy="afterInteractive"
    />
  );
};

const CookieConsentBanner = () => {
  const { bannerOpen, setConsent } = useCookieConsent();

  if (!bannerOpen) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[1000] border-t border-gray-200 bg-white p-4 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
      <div className="mx-auto flex max-w-5xl flex-col items-start gap-4 sm:flex-row sm:items-center">
        <p className="flex-1 text-sm text-gray-700">
          We use essential cookies to keep you signed in. With your permission, we also use Google
          Analytics and Google Ads cookies to understand how our site is used and to show you
          relevant ads on other sites. See our{' '}
          <Link href={PageLink.privacy} className="text-primary-600 underline">
            Privacy Policy
          </Link>{' '}
          for details.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            className="border-gray-300 text-gray-700"
            onClick={() => setConsent('denied')}>
            Decline
          </Button>
          <Button className="bg-primary-600" onClick={() => setConsent('granted')}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
